import React, { useEffect, useMemo, useState } from 'react';

import { ReactComponent as EmptyChargePointIcon } from '../../../assets/icons/EmptyChargePointIcon.svg';
import { ReactComponent as NextArrow } from '../../../assets/icons/next-arrow.svg';
import { ReactComponent as PreviousArrow } from '../../../assets/icons/prev-arrow.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import vlbcApi from '../../../utils/api/vlbc';

import DashedLineComponent from './animation-components/DashedLineComponent';
import DashedRing from './animation-components/DashedRing';
import DeviceCard from './DeviceCard';

interface DeviceChargerProperties {
  vlbcId: string;
}
interface Connector {
  _id: string;
  connectorId?: number;
  isReservationEnabled?: boolean;
  status?: string;
  isAvailable?: boolean;
  normalizedStatus?: string;
  userCustomId?: string;
  vehicleManufacturer?: string;
  vehicleModel?: string;
  chargingDurationInMinutes?: number;
}

interface ChargingPoint {
  _id?: string;
  chargingPointName?: string;
  chargingPointSn?: string;
  maximumOutputPower?: number;
  maximumOutputPowerUnit?: string;
  status: string;
  connectors?: Connector[];
  allocated?: string | number;
  available?: string | number;
  use?: string | number;
}

interface VlbcData {
  _id: string;
  loadBalancerName: string;
  algorithm: string;
  loadBalancerType: string;
  maximumOutputPowerUnit: string;
  chargingPoints: ChargingPoint[];
  allocated?: number;
  available?: number;
  use?: number;
  createdAt?: string;
}

/**
 * Normalizes the status by converting to lowercase and removing whitespace.
 */
const normalizeStatus = (status: string): string => status.toLowerCase().replace(/\s+/g, '');

/**
 * Returns a corresponding color code based on the given status.
 */
const getColor = (status: string): string => {
  const normalized = normalizeStatus(status);
  switch (normalized) {
    case 'inprogress':
      return '#E9ECEF';
    case 'fault':
      return '#E41010';
    case 'unavailable':
      return '#6C757D';
    case 'free':
      return '#FCE017';
    case 'finishing':
      return '#00A79D';
    case 'preparing':
      return '#F79009';
    case 'reserved':
      return '#4ABDFB';
    case 'suspendedev':
      return '#B3261E';
    case 'suspendedevsc':
      return '#B3261E';
    default:
      return '#E9ECEF';
  }
};

/**
 * Determines whether the status should trigger an animation.
 */
const shouldAnimateStatus = (status: string): boolean => {
  return normalizeStatus(status) === 'inprogress';
};

/**
 * Splits an array into smaller chunks of a specified size.
 */
const chunkArray = <T,>(array: T[], size: number): T[][] => {
  if (!array || array.length === 0) return [];
  const chunks: T[][] = [];
  for (let index = 0; index < array.length; index += size) {
    chunks.push(array.slice(index, index + size));
  }
  return chunks;
};

/**
 * side DeviceCharger component
 */
function DeviceCharger({ vlbcId }: DeviceChargerProperties) {
  const dispatch = useAppDispatch();
  const [vlbcIdData, setVlbcIdData] = useState<VlbcData[]>([]);
  const chunkSize = 3;
  const [currentPage, setCurrentPage] = useState(0); // Single page state for all items
  const [zoomLevel, setZoomLevel] = useState(0.7);
  const chargingPoints = vlbcIdData.length > 0 ? vlbcIdData[0].chargingPoints : [];
  const chunks = useMemo(() => chunkArray(chargingPoints, chunkSize), [chargingPoints, chunkSize]);
  const currentChunk = chunks[currentPage] || [];
  const totalPages = chunks.length;



  useEffect(() => {
  if (vlbcId) {
    vlbcApi
      .fetchVlbcOverview(vlbcId)
      .then((response: { data: VlbcData[] | VlbcData }) => {
        let rawData = response.data;
        // Normalize: if the data is wrapped in a property (like 'vlbc'), unwrap it
        if (Array.isArray(rawData)) {
          rawData = rawData.map((item) => {
            // If item has a 'vlbc' property, use it; otherwise, use item itself
            if (item && typeof item === 'object' && 'vlbc' in item) {
              return (item as any).vlbc;
            }
            return item;
          });
        } else if (rawData && typeof rawData === 'object' && 'vlbc' in rawData) {
          rawData = [(rawData as any).vlbc];
        } else {
          rawData = [rawData];
        }
        setVlbcIdData(rawData as VlbcData[]);
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  }
}, [vlbcId]);

  /**
   * Advances to the next page if not already on the last page.
   */
  const goToNextPage = () => {
    setCurrentPage((previous) => (previous < totalPages - 1 ? previous + 1 : previous));
  };

  /**
   * Goes back to the previous page if not already on the first page.
   */
  const goToPreviousPage = () => {
    setCurrentPage((previous) => (previous > 0 ? previous - 1 : previous));
  };

  /**
   * Increases the zoom level
   */
  const zoomIn = () => {
    setZoomLevel((previous) => Math.min(previous + 0.1, 2)); // Limit max zoom to 2x
  };

  /**
   * Decreases the zoom level
   */
  const zoomOut = () => {
    setZoomLevel((previous) => Math.max(previous - 0.1, 0.5)); // Limit min zoom to 0.5x
  };

  /**
   * Resets the zoom level to default
   */
  const resetZoom = () => {
    setZoomLevel(1);
  };

  const originalHeight = 1000;
  let containerClass = '';
  if (currentChunk?.length === 1) {
    containerClass = 'containerFirst';
  } else if (currentChunk?.length === 2) {
    containerClass = 'containerSecond';
  } else if (currentChunk?.length === 3) {
    containerClass = 'containerThird';
  }

  
 
  return (
    <div className="vlbc-overview-container">
      {vlbcIdData.length !== 0 && (
        <div className="vlbc-overview-wrapper">
          <div className="device-tabs-container">Load Balancer Over View</div>

          <div className="zoom-controls">
            <button type="button" onClick={zoomOut} disabled={zoomLevel <= 0.5}>
              -
            </button>
            <button type="button" onClick={resetZoom}>
              {Math.round(zoomLevel * 100)}%
            </button>
            <button type="button" onClick={zoomIn} disabled={zoomLevel >= 2}>
              +
            </button>
          </div>
          <div
            style={{
              transform: `scale(${zoomLevel})`,
              transformOrigin: 'top left',
              transition: 'transform 0.2s ease',
              width: '100%',
              height: `${originalHeight * zoomLevel}px`,
            }}>
            {vlbcIdData.map((item) => (
              <div key={`vlbc-card-${item}`} className="slides-main">
                <div className="static-charger-wrapper">
                  <div className="circle-border">
                    <div className="dashed-ring">
                      <DashedRing
                        size={160}
                        thickness={2}
                        dashColor="#d3d3d3"
                        activeColor="#00c389"
                        dashCount={20}
                        animationDuration={10}
                        isAnimating
                        className=""
                      />
                    </div>
                    <div className="image-container">
                      <EmptyChargePointIcon />
                    </div>
                    <div className="content">
                      <div className="size-label">{item.loadBalancerName}</div>
                      <div className="size-label">{item.algorithm}</div>
                      <div className="size-label">
                        Available <span className="label-colour"> {item.available}Amps</span>
                      </div>
                      <div className="size-label">
                        Use{' '}
                        <span className="label-colour">
                          {item.use}
                          {item.maximumOutputPowerUnit === 'A' ? (
                            <span>Amps</span>
                          ) : (
                            <span>kWh</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="static-charger-main">
                  <div className={`static-charger-wrapper-container ${containerClass}`}>
                    {currentChunk?.length === 3 &&
                      currentChunk.map((device: ChargingPoint, index) => {
                        const module = (index + 1) % 3;

                        if (module === 1) {
                          return (
                            <div key={`device-${device._id}`}>
                              <div className="static-horizontal-first-top">
                                <DashedLineComponent
                                  color={getColor(device.status)}
                                  width="5px"
                                  height="50px"
                                  dashSpacing="10px"
                                  shape="horizontal"
                                  animate={shouldAnimateStatus(device.status)}
                                  animationDelay={0}
                                  animationColor="#38AAE7"
                                  dotsCountValue={4}
                                />
                              </div>
                              <div className="static-vertical-first-left">
                                <DashedLineComponent
                                  color={getColor(device.status)}
                                  width="220px"
                                  height="5px"
                                  dashSpacing="5px"
                                  shape="vertical"
                                  animate={shouldAnimateStatus(device.status)}
                                  animationDelay={500}
                                  animationColor="#38AAE7"
                                  dotsCountValue={12}
                                />
                              </div>
                              <div className="static-horizontal-first-top-rigth">
                                <DashedLineComponent
                                  color={getColor(device.status)}
                                  width="5px"
                                  height="50px"
                                  dashSpacing="10px"
                                  shape="horizontal"
                                  animate={shouldAnimateStatus(device.status)}
                                  animationDelay={20}
                                  animationColor="#38AAE7"
                                  dotsCountValue={4}
                                />
                              </div>
                              <DeviceCard
                                borderClass={device?.status ?? ''}
                                title={device?.chargingPointName ?? ''}
                                allocated={device?.allocated ?? '-'}
                                available={device?.available ?? '-'}
                                used={device?.use ?? '-'}
                                array={
                                  device?.connectors?.map((connector: Connector) => ({
                                    _id: connector?._id ?? '',
                                    status: connector.normalizedStatus ?? '',
                                    connectorId: connector?.connectorId,
                                    userCustomId: connector?.userCustomId,
                                    vehicleManufacturer: connector?.vehicleManufacturer,
                                    vehicleModel: connector?.vehicleModel,
                                    value: connector?.chargingDurationInMinutes,
                                  })) || []
                                }
                              />
                            </div>
                          );
                        }

                        if (module === 2) {
                          return (
                            <div key={`device-${device._id}`}>
                              <div className="static-horizontal-secount-top">
                                <DashedLineComponent
                                  color={getColor(device.status)}
                                  width="5px"
                                  height="95px"
                                  dashSpacing="10px"
                                  shape="horizontal"
                                  animate={shouldAnimateStatus(device.status)}
                                  animationDelay={20}
                                  animationColor="#38AAE7"
                                  dotsCountValue={5}
                                />
                              </div>
                              <DeviceCard
                                borderClass={device?.status ?? ''}
                                title={device?.chargingPointName ?? ''}
                                allocated={device?.allocated ?? '-'}
                                available={device?.available ?? '-'}
                                used={device?.use ?? '-'}
                                array={
                                  device?.connectors?.map((connector: Connector) => ({
                                    _id: connector?._id ?? '',
                                    status: connector.normalizedStatus ?? '',
                                    connectorId: connector?.connectorId,
                                    userCustomId: connector?.userCustomId,
                                    vehicleManufacturer: connector?.vehicleManufacturer,
                                    vehicleModel: connector?.vehicleModel,
                                    value: connector?.chargingDurationInMinutes,
                                  })) || []
                                }
                              />
                            </div>
                          );
                        }

                        if (module === 0) {
                          return (
                            <div key={`device-${device._id}`}>
                              <div className="static-horizontal-third-top">
                                <DashedLineComponent
                                  color={getColor(device.status)}
                                  width="5px"
                                  height="49px"
                                  dashSpacing="10px"
                                  shape="horizontal"
                                  animate={shouldAnimateStatus(device.status)}
                                  animationDelay={20}
                                  animationColor="#38AAE7"
                                  dotsCountValue={3}
                                />
                              </div>
                              <div className="static-vertical-third-left">
                                <DashedLineComponent
                                  color={getColor(device.status)}
                                  width="220px"
                                  height="5px"
                                  dashSpacing="5px"
                                  shape="vertical"
                                  animate={shouldAnimateStatus(device.status)}
                                  animationDelay={20}
                                  animationColor="#38AAE7"
                                  dotsCountValue={12}
                                />
                              </div>
                              <div className="static-horizontal-third-top-rigth">
                                <DashedLineComponent
                                  color={getColor(device.status)}
                                  width="5px"
                                  height="48px"
                                  dashSpacing="10px"
                                  shape="horizontal"
                                  animate={shouldAnimateStatus(device.status)}
                                  animationDelay={20}
                                  animationColor="#38AAE7"
                                  dotsCountValue={4}
                                />
                              </div>
                              <DeviceCard
                                borderClass={device?.status ?? ''}
                                title={device?.chargingPointName ?? ''}
                                allocated={device?.allocated ?? '-'}
                                available={device?.available ?? '-'}
                                used={device?.use ?? '-'}
                                array={
                                  device?.connectors?.map((connector: Connector) => ({
                                    _id: connector?._id ?? '',
                                    status: connector.normalizedStatus ?? '',
                                    connectorId: connector?.connectorId,
                                    userCustomId: connector?.userCustomId,
                                    vehicleManufacturer: connector?.vehicleManufacturer,
                                    vehicleModel: connector?.vehicleModel,
                                    value: connector?.chargingDurationInMinutes,
                                  })) || []
                                }
                              />
                            </div>
                          );
                        }

                        return null;
                      })}
                    {currentChunk?.length === 1 &&
                      currentChunk.map((device: ChargingPoint, index) => {
                        const module = (index + 1) % 3;

                        if (module === 1) {
                          return (
                            <div key={`device-${device._id}`}>
                              <div className="containerFirst-horizontal">
                                <DashedLineComponent
                                  color={getColor(device.status)}
                                  width="5px"
                                  height="95px"
                                  dashSpacing="10px"
                                  shape="horizontal"
                                  animate={shouldAnimateStatus(device.status)}
                                  animationDelay={20}
                                  animationColor="#38AAE7"
                                  dotsCountValue={5}
                                />
                              </div>
                              <DeviceCard
                                borderClass={device?.status ?? ''}
                                title={device?.chargingPointName ?? ''}
                                allocated={device?.allocated ?? '-'}
                                available={device?.available ?? '-'}
                                used={device?.use ?? '-'}
                                array={
                                  device?.connectors?.map((connector: Connector) => ({
                                    _id: connector?._id ?? '',
                                    status: connector.normalizedStatus ?? '',
                                    connectorId: connector?.connectorId,
                                    userCustomId: connector?.userCustomId,
                                    vehicleManufacturer: connector?.vehicleManufacturer,
                                    vehicleModel: connector?.vehicleModel,
                                    value: connector?.chargingDurationInMinutes,
                                  })) || []
                                }
                              />
                            </div>
                          );
                        }

                        return null;
                      })}
                    {currentChunk?.length === 2 &&
                      currentChunk.map((device: ChargingPoint, index) => {
                        const module = (index + 1) % 3;

                        if (module === 1) {
                          return (
                            <div key={`device-${device._id}`}>
                              <div className="containerSecond-horizontal-first-top">
                                <DashedLineComponent
                                  color={getColor(device.status)}
                                  width="5px"
                                  height="50px"
                                  dashSpacing="10px"
                                  shape="horizontal"
                                  animate={shouldAnimateStatus(device.status)}
                                  animationDelay={0}
                                  animationColor="#38AAE7"
                                  dotsCountValue={4}
                                />
                              </div>
                              <div className="containerSecond-vertical-first-left">
                                <DashedLineComponent
                                  color={getColor(device.status)}
                                  width="100px"
                                  height="5px"
                                  dashSpacing="5px"
                                  shape="vertical"
                                  animate={shouldAnimateStatus(device.status)}
                                  animationDelay={500}
                                  animationColor="#38AAE7"
                                  dotsCountValue={12}
                                />
                              </div>
                              <div className="static-horizontal-first-top-rigth">
                                <DashedLineComponent
                                  color={getColor(device.status)}
                                  width="5px"
                                  height="50px"
                                  dashSpacing="10px"
                                  shape="horizontal"
                                  animate={shouldAnimateStatus(device.status)}
                                  animationDelay={20}
                                  animationColor="#38AAE7"
                                  dotsCountValue={4}
                                />
                              </div>
                              <DeviceCard
                                borderClass={device?.status ?? ''}
                                title={device?.chargingPointName ?? ''}
                                allocated={device?.allocated ?? '-'}
                                available={device?.available ?? '-'}
                                used={device?.use ?? '-'}
                                array={
                                  device?.connectors?.map((connector: Connector) => ({
                                    _id: connector?._id ?? '',
                                    status: connector.normalizedStatus ?? '',
                                    connectorId: connector?.connectorId,
                                    userCustomId: connector?.userCustomId,
                                    vehicleManufacturer: connector?.vehicleManufacturer,
                                    vehicleModel: connector?.vehicleModel,
                                    value: connector?.chargingDurationInMinutes,
                                  })) || []
                                }
                              />
                            </div>
                          );
                        }

                        if (module === 2) {
                          return (
                            <div key={`device-${device._id}`}>
                              <div className="static-horizontal-third-top">
                                <DashedLineComponent
                                  color={getColor(device.status)}
                                  width="5px"
                                  height="49px"
                                  dashSpacing="10px"
                                  shape="horizontal"
                                  animate={shouldAnimateStatus(device.status)}
                                  animationDelay={20}
                                  animationColor="#38AAE7"
                                  dotsCountValue={3}
                                />
                              </div>
                              <div className="containerSecond-vertical-third-left">
                                <DashedLineComponent
                                  color={getColor(device.status)}
                                  width="80px"
                                  height="5px"
                                  dashSpacing="5px"
                                  shape="vertical"
                                  animate={shouldAnimateStatus(device.status)}
                                  animationDelay={20}
                                  animationColor="#38AAE7"
                                  dotsCountValue={12}
                                />
                              </div>
                              <div className="containerSecond-horizontal-third-top-rigth">
                                <DashedLineComponent
                                  color={getColor(device.status)}
                                  width="5px"
                                  height="48px"
                                  dashSpacing="10px"
                                  shape="horizontal"
                                  animate={shouldAnimateStatus(device.status)}
                                  animationDelay={20}
                                  animationColor="#38AAE7"
                                  dotsCountValue={4}
                                />
                              </div>
                              <DeviceCard
                                borderClass={device?.status ?? ''}
                                title={device?.chargingPointName ?? ''}
                                allocated={device?.allocated ?? '-'}
                                available={device?.available ?? '-'}
                                used={device?.use ?? '-'}
                                array={
                                  device?.connectors?.map((connector: Connector) => ({
                                    _id: connector?._id ?? '',
                                    status: connector.normalizedStatus ?? '',
                                    connectorId: connector?.connectorId,
                                    userCustomId: connector?.userCustomId,
                                    vehicleManufacturer: connector?.vehicleManufacturer,
                                    vehicleModel: connector?.vehicleModel,
                                    value: connector?.chargingDurationInMinutes,
                                  })) || []
                                }
                              />
                            </div>
                          );
                        }

                        return null;
                      })}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="pagination-container">
              <button
                type="button"
                className="prev-button"
                onClick={goToPreviousPage}
                disabled={currentPage === 0}
                aria-label="Previous Slide">
                <PreviousArrow />
              </button>

              <div className="pagination-info">
                <span className="page-count">
                  Page {currentPage + 1} of {totalPages}
                </span>
                <span className="device-count">
                  ({currentChunk.length} of {chargingPoints.length} devices)
                </span>
              </div>

              <button
                type="button"
                className="next-button"
                onClick={goToNextPage}
                disabled={currentPage >= totalPages - 1}
                aria-label="Next Slide">
                <NextArrow />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DeviceCharger;
